import { fmtAge, shortKey } from './healthFormat';
import { Stat } from './healthPrimitives';
import type { FillsPollerInfo, G1HealthSnapshot, OrgCacheHealth, OrgWsHealth } from './types';

// Feed liveness — the "is the pipeline alive end to end?" board. Absorbs the
// old Org-broker-cache table and joins it with the WS fills sockets and the
// REST fills backstop into ONE row per org, under a tile row for the push
// channel / settlement sweeper / shadow verifier. This is the real-time
// answer to "are the feeds live?" without ssh-ing into a box.
export function FeedLiveness({
  snap,
  orgNames,
  inSel,
}: {
  snap: G1HealthSnapshot;
  orgNames: Record<string, string>;
  inSel: (orgId: string | null) => boolean;
}) {
  const push = snap.push;
  const sweep = snap.settlementSweep;
  const shadow = snap.positionStates?.shadow;
  const mode = snap.positionStates?.mode;

  // One row per org: union of the three per-org health sources.
  const byOrg = new Map<
    string,
    {
      poller?: OrgCacheHealth['orgs'][number];
      ws?: OrgWsHealth;
      fills?: FillsPollerInfo;
    }
  >();
  const rowFor = (orgId: string) => {
    const r = byOrg.get(orgId) ?? {};
    byOrg.set(orgId, r);
    return r;
  };
  for (const o of snap.orgCache.orgs) if (inSel(o.orgId)) rowFor(o.orgId).poller = o;
  for (const w of snap.kalshiWs ?? []) if (inSel(w.orgId)) rowFor(w.orgId).ws = w;
  for (const f of snap.fillsPoller ?? []) if (inSel(f.orgId)) rowFor(f.orgId).fills = f;
  const rows = [...byOrg.entries()].sort((a, b) => {
    const an = orgNames[a[0]] ?? a[0];
    const bn = orgNames[b[0]] ?? b[0];
    return an.localeCompare(bn);
  });

  const suppressionPct =
    push && push.sent + push.suppressed > 0
      ? Math.round((push.suppressed / (push.sent + push.suppressed)) * 100)
      : null;
  const pollMs = snap.orgPollMs;

  return (
    <section className="border-border rounded-lg border p-4">
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-foreground text-sm font-semibold">Feed liveness</h2>
        <span
          className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${
            snap.orgCache.polling ? 'bg-green-600/80 text-white' : 'bg-muted text-muted-foreground'
          }`}
        >
          {snap.orgCache.polling ? 'POLLING' : 'IDLE'}
        </span>
        <span className="text-muted-foreground text-[11px]">
          per org: shared {pollMs != null ? `${pollMs}ms` : ''} order/queue poller · WS fills socket · REST fills
          backstop
        </span>
      </div>

      {/* Pipeline tile row — the fan-out side (relay → browsers) + background loops. */}
      <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-5">
        <Stat
          label="Push channel"
          value={push ? `${push.connections}` : '—'}
          sub={push ? `conns · ${push.users} users` : 'not reported'}
        />
        <Stat
          label="Push suppression"
          value={suppressionPct != null ? `${suppressionPct}%` : '—'}
          sub={push ? `${push.sent.toLocaleString()} sent · ${push.suppressed.toLocaleString()} suppressed` : undefined}
        />
        <Stat
          label="Resolver errors"
          value={push ? String(push.resolverErrors) : '—'}
          tone={push && push.resolverErrors > 0 ? 'danger' : 'normal'}
          sub="push topic resolves failed"
        />
        <Stat
          label="Settlement sweep"
          value={sweep ? (sweep.running ? 'on' : 'off') : '—'}
          tone={sweep && sweep.consecutiveFailures > 0 ? 'warn' : 'normal'}
          sub={
            sweep?.lastRunAt
              ? `${fmtAge(Math.max(0, snap.serverTs - sweep.lastRunAt))} ago · ${sweep.lastSettlementsCredited} credited${sweep.consecutiveFailures > 0 ? ` · ${sweep.consecutiveFailures} fails` : ''}`
              : 'no run yet'
          }
        />
        <Stat
          label="Shadow parity"
          value={shadow ? String(shadow.divergences) : '—'}
          tone={shadow && shadow.divergences > 0 ? 'danger' : 'normal'}
          sub={shadow ? `divergences · mode ${mode ?? '?'} · ${shadow.usersChecked} checked` : undefined}
        />
      </div>

      {rows.length === 0 ? (
        <p className="text-muted-foreground py-3 text-center text-xs">No active orgs right now.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs font-mono">
            <thead className="text-muted-foreground border-border border-b">
              <tr>
                <th className="py-1.5 pr-3 font-medium">Org</th>
                <th className="px-3 text-right font-medium">Orders cached</th>
                <th className="px-3 text-center font-medium" title="order/queue poller snapshot state">Poller</th>
                <th className="px-3 text-right font-medium" title="poller snapshot age — should hover near the poll cadence">Age</th>
                <th className="px-3 text-right font-medium" title="consecutive failed polls">Fails</th>
                <th className="px-3 text-right font-medium" title="empty-overwrite guards tripped">Empty-guards</th>
                <th className="px-3 text-center font-medium" title="Kalshi user-fills WebSocket">WS fills</th>
                <th className="px-3 text-right font-medium" title="WS: reconnects · sequence gaps">rc/gaps</th>
                <th className="px-3 text-right font-medium" title="fills ingested over this socket's lifetime">WS fills #</th>
                <th className="px-3 text-center font-medium" title="REST fills poller: primary (WS down/off) or backstop (WS healthy)">REST fills</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([orgId, r]) => {
                const stale = r.poller && !r.poller.ok;
                const wsUp = r.ws?.connected && r.ws?.subscribed;
                return (
                  <tr key={orgId} className={`border-border/50 border-b ${stale ? 'bg-red-500/10' : ''}`}>
                    <td className="text-foreground py-1.5 pr-3">{orgNames[orgId] ?? shortKey(`org:${orgId}`).id}</td>
                    <td className="text-foreground px-3 text-right">{r.poller?.orderCount ?? '—'}</td>
                    <td className="px-3 text-center">
                      {r.poller ? (
                        r.poller.ok ? (
                          <span className="text-green-400">ok</span>
                        ) : (
                          <span className="font-bold text-red-400">STALE</span>
                        )
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="text-muted-foreground px-3 text-right">
                      {r.poller ? fmtAge(r.poller.ageMs) : '—'}
                    </td>
                    <td className={`px-3 text-right ${r.poller && r.poller.consecutiveFailures > 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
                      {r.poller?.consecutiveFailures ?? '—'}
                    </td>
                    <td className={`px-3 text-right ${r.poller && r.poller.suspectedEmptyOverwrites > 0 ? 'text-amber-400' : 'text-muted-foreground'}`}>
                      {r.poller?.suspectedEmptyOverwrites ?? '—'}
                    </td>
                    <td className="px-3 text-center">
                      {r.ws ? (
                        wsUp ? (
                          <span
                            className="text-green-400"
                            title={`key ${r.ws.keyLabel ?? '?'} · last event ${r.ws.lastEventAgeMs != null ? fmtAge(r.ws.lastEventAgeMs) : '—'} ago`}
                          >
                            LIVE
                          </span>
                        ) : (
                          <span className="font-bold text-red-400" title="socket down or not subscribed — REST poller should be primary">
                            DOWN
                          </span>
                        )
                      ) : (
                        <span className="text-muted-foreground" title="KALSHI_WS_FILLS off or no socket for this org">—</span>
                      )}
                    </td>
                    <td className={`px-3 text-right ${r.ws && (r.ws.seqGaps > 0 || r.ws.reconnects > 0) ? 'text-amber-400' : 'text-muted-foreground'}`}>
                      {r.ws ? `${r.ws.reconnects}/${r.ws.seqGaps}` : '—'}
                    </td>
                    <td className="text-muted-foreground px-3 text-right">{r.ws?.fillsIngested ?? '—'}</td>
                    <td className="px-3 text-center">
                      {r.fills ? (
                        <span
                          className={r.fills.mode === 'backstop' ? 'text-green-400' : 'text-amber-400'}
                          title={`last poll ${r.fills.lastPollAt ? fmtAge(Math.max(0, snap.serverTs - r.fills.lastPollAt)) + ' ago' : 'never'} · last inserted ${r.fills.lastInserted}. backstop = WS is primary (good); primary = REST carrying fills (WS down/off).`}
                        >
                          {r.fills.mode}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-muted-foreground mt-2 text-[11px]">
        Healthy shape: poller <span className="text-foreground">ok</span> with age ≈ the poll cadence, WS{' '}
        <span className="text-foreground">LIVE</span> with rc/gaps 0/0, REST fills{' '}
        <span className="text-foreground">backstop</span>, suppression high (quiet books send nothing), resolver
        errors 0, shadow divergences 0.
      </p>
    </section>
  );
}
